'use client';

import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import {
  getNativeLocaleLabel,
  SUPPORTED_LOCALES,
  normalizeLocale,
} from '@/i18n/config';
import { getLocalizedMarketingDestination } from '@/components/marketing/marketing-language-switcher';
import { setPendingSignupLocale } from '@/lib/auth-locale-preference';
import { MarketingSquare } from './section-label';

/**
 * Language picker for logged-out pages.
 *
 * Reuses the existing marketing locale plumbing rather than reimplementing it:
 * `getLocalizedMarketingDestination` builds the prefixed URL the proxy expects,
 * and `setPendingSignupLocale` carries the choice through signup so a visitor
 * who picks Japanese and then registers lands in Japanese.
 *
 * Any un-authed page can drop this in — it derives everything from the current
 * route, so it needs no props and no per-page wiring.
 *
 * The trigger and every option show each language's native name.
 */
export function MarketingLocaleSwitcher({
  className,
  variant = 'desktop',
}: {
  className?: string;
  variant?: 'desktop' | 'mobile';
}) {
  const locale = normalizeLocale(useLocale());
  const pathname = usePathname() ?? '/';
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen]);

  const selectLocale = (target: string) => {
    setIsOpen(false);
    setPendingSignupLocale(target);
    // A full navigation, not a client transition: the locale lives in the URL
    // prefix and the proxy has to re-run to set the cookie and rewrite.
    window.location.assign(
      getLocalizedMarketingDestination({
        pathname,
        targetLocale: target,
        search: window.location.search,
        hash: window.location.hash,
      })
    );
  };

  if (variant === 'mobile') {
    return (
      <div className={cn('relative w-full shrink-0', className)}>
        <select
          value={locale}
          aria-label={getNativeLocaleLabel(locale)}
          onChange={(event) => selectLocale(event.target.value)}
          className="bg-marketing-chip text-marketing-chip-fg focus-visible:outline-marketing-accent-strong min-h-11 w-full cursor-pointer appearance-none rounded-sm py-3 ps-2.5 pe-10 text-xs leading-none font-semibold outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          {SUPPORTED_LOCALES.map((option) => (
            <option key={option} value={option}>
              {getNativeLocaleLabel(option)}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="text-marketing-chip-fg pointer-events-none absolute end-2.5 top-1/2 size-4 -translate-y-1/2"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative shrink-0', className)}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setIsOpen((open) => !open)}
        className="focus-visible:outline-marketing-accent-strong peer flex cursor-pointer items-center gap-2 text-xs font-medium [letter-spacing:-0.01em] outline-none focus-visible:outline-2 focus-visible:outline-offset-4 lg:text-sm"
      >
        <MarketingSquare
          size="sm"
          className={cn(
            'transition-all',
            isOpen ? 'bg-marketing-fg-subtle' : 'bg-transparent'
          )}
        />
        <span
          className={cn(
            'text-marketing-fg hover:text-marketing-fg/80 block font-medium transition-all',
            isOpen ? 'translate-x-0' : '-translate-x-4'
          )}
        >
          {getNativeLocaleLabel(locale)}
        </span>
      </button>
      <div
        aria-hidden="true"
        className={cn(
          'bg-marketing-fg/60 pointer-events-none absolute bottom-0 left-0 h-0.5 transition-[width] duration-200 motion-reduce:transition-none',
          isOpen
            ? 'w-full'
            : 'w-0 peer-hover:w-[calc(100%_-_0.9rem)] peer-focus-visible:w-[calc(100%_-_1rem)]'
        )}
      />

      <ul
        id={menuId}
        role="menu"
        inert={!isOpen}
        className={cn(
          'marketing-locale-menu bg-marketing-bg absolute top-full -left-3 z-10 mt-2 max-h-80 w-max min-w-60 origin-top overflow-y-auto rounded-lg px-3',
          'transition-all duration-200 ease-out motion-reduce:transition-none',
          isOpen
            ? 'translate-y-2 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0'
        )}
      >
        {SUPPORTED_LOCALES.filter((option) => option !== locale).map(
          (option) => (
            <li key={option} role="none">
              <button
                type="button"
                role="menuitem"
                lang={option}
                onClick={() => selectLocale(option)}
                className="text-marketing-fg hover:text-marketing-fg/70 focus-visible:outline-marketing-accent-strong block w-full cursor-pointer py-2 text-start text-xs font-medium [letter-spacing:-0.01em] whitespace-nowrap transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 lg:text-sm"
              >
                <bdi>{getNativeLocaleLabel(option)}</bdi>
              </button>
            </li>
          )
        )}
      </ul>
    </div>
  );
}
